import { FunctionComponent, useEffect, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { DownloadOutlined } from '@mui/icons-material'
import { DialogWrapper } from './dialog-wrapper'
import { LoadingSpinner } from './loading-spinner'
import UploadedResourceService from '../../../shared/services/uploaded-resource/uploaded-resource.service'
import { IEmissionDocument } from '../../../shared/services/emission/emission.model'

const PDF_TYPES = ['application/pdf']
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']

/** Whether the browser can show this document inline (PDF or image). Anything else downloads instead. */
export const isPreviewableDocument = (document: Pick<IEmissionDocument, 'mimetype' | 'filename'>): boolean => {
  const type = (document.mimetype || '').toLowerCase()
  if (PDF_TYPES.includes(type) || IMAGE_TYPES.includes(type)) return true
  return /\.(pdf|png|jpe?g|gif|webp)$/i.test(document.filename || '')
}

/**
 * Shows one uploaded document inline: PDFs in an embedded viewer, images as a picture. The file is
 * fetched through the same org-scoped download route as the download button, so nothing is exposed
 * that could not already be downloaded.
 */
export const DocumentPreviewDialog: FunctionComponent<{
  open: boolean,
  onClose: () => void,
  document: IEmissionDocument | null,
}> = ({ open, onClose, document }) => {
  const uploadedResourceService = new UploadedResourceService()
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [failed, setFailed] = useState<boolean>(false)

  const isPdf = !!document && (PDF_TYPES.includes((document.mimetype || '').toLowerCase()) || /\.pdf$/i.test(document.filename || ''))

  useEffect(() => {
    if (!open || !document) return
    let isActive = true
    let url: string | null = null
    setIsLoading(true)
    setFailed(false)
    setObjectUrl(null)
    uploadedResourceService.getDownloadBlob(document.id)
      .then((blob) => {
        if (!isActive) return
        // Re-type the blob from the stored mimetype so the browser renders it rather than offering a download.
        const typed = document.mimetype && blob.type !== document.mimetype ? new Blob([blob], { type: document.mimetype }) : blob
        url = URL.createObjectURL(typed)
        setObjectUrl(url)
      })
      .catch((err) => {
        console.error(err)
        if (isActive) setFailed(true)
      })
      .finally(() => { if (isActive) setIsLoading(false) })
    return () => {
      isActive = false
      if (url) URL.revokeObjectURL(url)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, document?.id])

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title={document?.filename || 'Document'}
      cancelHandler={onClose}
      submitHandler={onClose}
      isSubmitting={false}
      hideCancelButton
      submitButtonText='Close'
      maxWidth='lg'
    >
      <Stack spacing={1.5}>
        {document && (
          <Box>
            <Button
              variant='outlined'
              size='small'
              startIcon={<DownloadOutlined />}
              onClick={() => uploadedResourceService.downloadFile(document.id, document.filename)}
              sx={{ textTransform: 'none' }}
            >
              Download
            </Button>
          </Box>
        )}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
            <LoadingSpinner />
          </Box>
        )}
        {!isLoading && failed && (
          <Typography variant='body2' color='error'>The document could not be loaded. Try the download instead.</Typography>
        )}
        {!isLoading && !failed && objectUrl && document && (
          isPdf
            ? (
              <Box
                component='iframe'
                title={document.filename}
                src={objectUrl}
                sx={{ width: '100%', height: '70vh', border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'common.white' }}
              />
            )
            : (
              <Box sx={{ display: 'flex', justifyContent: 'center', border: 1, borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: 'common.white' }}>
                <Box component='img' src={objectUrl} alt={document.filename} sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
              </Box>
            )
        )}
        {!isLoading && !failed && !objectUrl && !document && (
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>No document selected.</Typography>
        )}
        {!isLoading && !failed && objectUrl && (
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            If the viewer stays blank, your browser blocks embedded files: use Download instead.
          </Typography>
        )}
      </Stack>
    </DialogWrapper>
  )
}
